import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import AccountAPI from '../../api/account';
import EnterpriseAccountAPI from '../../api/enterprise/account';
import { throwErrorMessage } from '../utils/api';
import { FEATURE_FLAGS } from '../../featureFlags';

const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isUpdating: false,
    isCheckoutInProcess: false,
  },
};

export const getters = {
  getAccount: $state => id => {
    return $state.records.find(record => record.id === Number(id)) || {};
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  isFeatureEnabledonAccount: ($state, _, __, rootGetters) => (
    id,
    featureName
  ) => {
    // If a user is SuperAdmin and has access to the account, then they would see all the available features
    const isUserASuperAdmin = rootGetters.getCurrentUser?.type === 'SuperAdmin';
    const featuresExceptions = [FEATURE_FLAGS.CALL_OUTGOING, FEATURE_FLAGS.MESSAGE_SEARCH];
    // Deve retornar true se o usuário for SuperAdmin e não estiver na lista de exceções de recursos
    if (isUserASuperAdmin && !featuresExceptions.includes(featureName)) {
      return true;
    }

    const { features = {} } =
      $state.records.find(record => record.id === Number(id)) || {};
    return features[featureName] || false;
  },
  isOpportunityPipelinesManagementAllowed: (
    $state,
    moduleGetters,
    __,
    rootGetters
  ) => id => {
    const isUserASuperAdmin = rootGetters.getCurrentUser?.type === 'SuperAdmin';
    if (isUserASuperAdmin) {
      return true;
    }

    if (rootGetters.getCurrentRole !== 'administrator') {
      return false;
    }

    return moduleGetters.isFeatureEnabledonAccount(id, 'opportunity_pipelines');
  },
  isOpportunityKanbanAccessAllowed: (
    $state,
    moduleGetters,
    __,
    rootGetters
  ) => id => {
    const isUserASuperAdmin = rootGetters.getCurrentUser?.type === 'SuperAdmin';
    if (isUserASuperAdmin) {
      return true;
    }

    const allowedRoles = [
      'administrator',
      'supervisor',
      'team_supervisor',
      'agent',
      'analyzer',
      'assistant',
      'seller',
    ];
    if (!allowedRoles.includes(rootGetters.getCurrentRole)) {
      return false;
    }

    return moduleGetters.isFeatureEnabledonAccount(id, 'opportunity_pipelines');
  },
  isOpportunityDashboardAccessAllowed: (
    $state,
    moduleGetters,
    __,
    rootGetters
  ) => id => {
    const isUserASuperAdmin = rootGetters.getCurrentUser?.type === 'SuperAdmin';
    if (isUserASuperAdmin) {
      return true;
    }

    const allowedRoles = ['administrator', 'supervisor'];
    if (!allowedRoles.includes(rootGetters.getCurrentRole)) {
      return false;
    }

    return moduleGetters.isFeatureEnabledonAccount(id, 'opportunity_pipelines');
  },
  isOpportunityOwnerFilterAllowed: ($state, moduleGetters, __, rootGetters) =>
    rootGetters.getCurrentUser?.type === 'SuperAdmin' ||
    rootGetters.getCurrentRole !== 'seller',
  isOpportunityEditAllowed: ($state, moduleGetters, __, rootGetters) => id => {
    const isUserASuperAdmin = rootGetters.getCurrentUser?.type === 'SuperAdmin';
    if (isUserASuperAdmin) {
      return true;
    }

    const allowedRoles = [
      'administrator',
      'agent',
      'seller',
      'supervisor',
      'team_supervisor',
    ];
    if (!allowedRoles.includes(rootGetters.getCurrentRole)) {
      return false;
    }

    return moduleGetters.isFeatureEnabledonAccount(id, 'opportunity_pipelines');
  },
};

export const actions = {
  get: async ({ commit }) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isFetchingItem: true });
    try {
      const response = await AccountAPI.get();
      commit(types.default.ADD_ACCOUNT, response.data);
      commit(types.default.SET_ACCOUNT_UI_FLAG, {
        isFetchingItem: false,
      });
    } catch (error) {
      commit(types.default.SET_ACCOUNT_UI_FLAG, {
        isFetchingItem: false,
      });
    }
  },
  update: async ({ commit }, updateObj) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: true });
    try {
      await AccountAPI.update('', updateObj);
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: false });
      throw new Error(error);
    }
  },
  create: async ({ commit }, accountInfo) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isCreating: true });
    try {
      const response = await AccountAPI.createAccount(accountInfo);
      const account_id = response.data.data.account_id;
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isCreating: false });
      return account_id;
    } catch (error) {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isCreating: false });
      throw error;
    }
  },

  checkout: async ({ commit }) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isCheckoutInProcess: true });
    try {
      const response = await EnterpriseAccountAPI.checkout();
      window.location = response.data.redirect_url;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isCheckoutInProcess: false });
    }
  },

  subscription: async ({ commit }) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isCheckoutInProcess: true });
    try {
      await EnterpriseAccountAPI.subscription();
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isCheckoutInProcess: false });
    }
  },
};

export const mutations = {
  [types.default.SET_ACCOUNT_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.default.ADD_ACCOUNT]: MutationHelpers.setSingleRecord,
  [types.default.EDIT_ACCOUNT]: MutationHelpers.update,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
